/**
 * The bridge between SQLite and React.
 *
 * The rule this hook exists to enforce: a tick appears ticked only after the write has
 * returned. There is no optimistic update anywhere in here. If a write fails, the
 * control stays in its old state and carries the error. The prototype showed a
 * reassuring label based on having attempted a write; this does the opposite.
 */
import { useCallback, useEffect, useState } from 'react';
import { store } from '../data/client.ts';
import type { Capabilities } from '../data/client.ts';
import { stateFromMap } from '../plan/types.ts';
import type { PlanState } from '../plan/types.ts';
import { Mirror } from '../data/mirror.ts';
import { buildViews } from '../plan/derived.ts';
import { applyTheme, isTheme } from './theme.ts';
import { META } from '../plan/data.ts';
import { MirrorScheduler } from '../data/mirrorScheduler.ts';
import type { MirrorState } from '../data/mirror.ts';

export type WriteStatus = 'idle' | 'writing' | 'failed';

export interface StoreApi {
  ready: boolean;
  durable: boolean;
  capabilities: Capabilities | null;
  openError: string | null;
  /** Pure snapshot for the plan functions. Replaced, never mutated, on every write. */
  state: PlanState;
  /** Keys with a write currently in flight. */
  pending: ReadonlySet<string>;
  /** Verbatim error for a key whose last write failed. */
  errorFor(key: string): string | null;
  /** Most recent write failure, for the live region. */
  lastFailure: string | null;
  setFlag(key: string, on: boolean): Promise<boolean>;
  put(key: string, value: unknown): Promise<boolean>;
  del(key: string): Promise<boolean>;
  mirror: MirrorState | null;
  queued: number | null;
  pushNow(): Promise<void>;
  refreshMirror(): Promise<void>;
  scheduler: MirrorScheduler | null;
}

export function useStore(): StoreApi {
  const [snapshot, setSnapshot] = useState<Map<string, unknown>>(new Map());
  const [ready, setReady] = useState(false);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [lastFailure, setLastFailure] = useState<string | null>(null);
  const [mirror, setMirror] = useState<MirrorState | null>(null);
  const [queued, setQueued] = useState<number | null>(null);
  const [scheduler, setScheduler] = useState<MirrorScheduler | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await store.open();
        if (cancelled) return;
        setCaps(c);

        // The whole table. At most a few thousand rows over sixteen weeks, and loading
        // it whole removes an entire class of partial-load bug from the plan functions.
        const rows = await store.rows();
        const map = new Map<string, unknown>();
        for (const r of rows) if (!r.deleted) map.set(r.k, JSON.parse(r.v));
        if (cancelled) return;
        setSnapshot(map);

        const t = map.get('setting:theme');
        applyTheme(isTheme(t) ? t : 'system');

        const m = new Mirror({
          deviceId: store.deviceId,
          unsynced: (n) => store.unsynced(n),
          unsyncedCount: () => store.unsyncedCount(),
          markSynced: (r) => store.markSynced(r),
          getMeta: (k) => store.getMeta(k),
          setMeta: (k, v) => store.setMeta(k, v),
        });
        // Derived tabs are computed here, where plan logic is available; mirror.ts
        // stays free of plan imports so the data layer never depends on plan content.
        m.buildViews = async () => {
          const raw = await store.rows();
          const live = new Map<string, unknown>();
          for (const r of raw) if (!r.deleted) live.set(r.k, JSON.parse(r.v));
          const sd = (live.get('setting:startDate') as string) ?? META.defaultStartDate;
          return buildViews(stateFromMap(live), raw, sd);
        };
        setMirror(await m.state());
        setQueued(await store.unsyncedCount());
        const sched = new MirrorScheduler(m);
        sched.onChange((s) => {
          setMirror(s);
          void store.unsyncedCount().then((n) => setQueued(n));
        });
        sched.start();
        if (cancelled) { sched.stop(); return; }
        setScheduler(sched);
      } catch (e) {
        if (!cancelled) {
          setOpenError(e instanceof Error ? e.message : String(e));
          setCaps(store.capabilities);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const mark = useCallback((key: string, on: boolean) => {
    setPending((p) => {
      const next = new Set(p);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  /** Runs a write and folds the CONFIRMED result into the snapshot. Never optimistic. */
  const write = useCallback(async (
    key: string, run: () => Promise<unknown>, apply: (m: Map<string, unknown>) => void,
  ): Promise<boolean> => {
    mark(key, true);
    setErrors((e) => { if (!e.has(key)) return e; const n = new Map(e); n.delete(key); return n; });
    try {
      await run();                               // resolves only once committed
      setSnapshot((prev) => {
        const next = new Map(prev);
        apply(next);
        if (key === 'setting:theme') {
          const t = next.get('setting:theme');
          applyTheme(isTheme(t) ? t : 'system');
        }
        return next;
      });
      void store.unsyncedCount().then((n) => setQueued(n)).catch(() => {});
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrors((e) => new Map(e).set(key, msg));
      setLastFailure(`Not saved. ${msg}`);
      return false;                              // snapshot untouched: the tick did not happen
    } finally {
      mark(key, false);
    }
  }, [mark]);

  const setFlag = useCallback((key: string, on: boolean) =>
    write(key, () => store.setFlag(key, on),
      (m) => { if (on) m.set(key, 1); else m.delete(key); }), [write]);

  const put = useCallback((key: string, value: unknown) =>
    write(key, () => store.put(key, value), (m) => m.set(key, value)), [write]);

  const del = useCallback((key: string) =>
    write(key, () => store.del(key), (m) => m.delete(key)), [write]);

  const refreshMirror = useCallback(async () => {
    try { setQueued(await store.unsyncedCount()); } catch { /* reported elsewhere */ }
  }, []);

  const pushNow = useCallback(async () => {
    if (!scheduler) return;
    setMirror(await scheduler.runNow());
    await refreshMirror();
  }, [scheduler, refreshMirror]);

  return {
    ready,
    durable: caps?.durable === true,
    capabilities: caps,
    openError,
    state: stateFromMap(snapshot),
    pending,
    errorFor: (k) => errors.get(k) ?? null,
    lastFailure,
    setFlag, put, del,
    mirror, queued, pushNow, refreshMirror, scheduler,
  };
}
